import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { AppointmentItem } from '../appointments/appointments-model'
import {
  isPermissionDenied,
  type OverviewRepository,
} from './overview-repository'
import {
  clinicTodayBounds,
  hasCompleteTodayCoverage,
  parseTodaySchedule,
  type ClinicDayBounds,
} from './overview-schedule'

type ScheduleState = {
  owner: string
  boundsKey: string | null
  status: 'loading' | 'ready' | 'forbidden' | 'error' | 'invalid'
  items: AppointmentItem[]
}

export type TodayScheduleState = {
  status: ScheduleState['status']
  items: AppointmentItem[]
  bounds: ClinicDayBounds | null
}

const COVERAGE_COHERENCE_SETTLE_MS = 2_000

export function useTodaySchedule(
  repository: OverviewRepository,
  tenantId: string,
  nowMs?: number
): TodayScheduleState {
  const generation = useRef(0)
  const [referenceNowMs, setReferenceNowMs] = useState(
    () => nowMs ?? Date.now()
  )
  const timeZone = useQuery({
    queryKey: ['overview', tenantId, 'schedule-time-zone'],
    queryFn: () => repository.getClinicTimeZone(tenantId),
  })
  const bounds = useMemo(
    () =>
      timeZone.data ? clinicTodayBounds(timeZone.data, referenceNowMs) : null,
    [referenceNowMs, timeZone.data]
  )
  const boundsKey = bounds
    ? `${bounds.startAtMs}:${bounds.endBeforeMs}:${bounds.timeZone}`
    : null
  const [state, setState, resume] = useRetainedRealtimeState<ScheduleState>(
    ['overview', tenantId, 'today', boundsKey],
    {
      owner: tenantId,
      boundsKey,
      status: 'loading',
      items: [],
    },
    (value) => value.status === 'ready'
  )

  useEffect(() => {
    if (!bounds) return
    const delay = Math.max(1_000, bounds.endBeforeMs - Date.now() + 1_000)
    const timeout = window.setTimeout(
      () => setReferenceNowMs(Date.now()),
      Math.min(delay, 2_147_483_647)
    )
    return () => window.clearTimeout(timeout)
  }, [bounds])

  useEffect(() => {
    const currentGeneration = ++generation.current
    resume()
    if (!bounds) return
    // Each read's failure clears when that read next delivers a value, so the
    // schedule recovers by itself once its reads succeed again. A refusal of
    // access stays until the schedule is opened again.
    let forbidden = false
    let appointmentsFailed = false
    let coverageFailed = false
    let appointmentsLoaded = false
    let coverageLoaded = false
    let appointmentValue: unknown = null
    let coverageValue: unknown = null
    let coherenceTimeout: number | null = null
    const clearCoherenceTimeout = () => {
      if (coherenceTimeout === null) return
      window.clearTimeout(coherenceTimeout)
      coherenceTimeout = null
    }
    const handleError =
      (source: 'appointments' | 'coverage') => (error: unknown) => {
        if (generation.current !== currentGeneration) return
        if (isPermissionDenied(error)) forbidden = true
        if (source === 'appointments') appointmentsFailed = true
        else coverageFailed = true
        clearCoherenceTimeout()
        setState({
          owner: tenantId,
          boundsKey,
          status: forbidden ? 'forbidden' : 'error',
          items: [],
        })
      }
    const commitInvalid = () => {
      if (generation.current !== currentGeneration) return
      coherenceTimeout = null
      setState({
        owner: tenantId,
        boundsKey,
        status: 'invalid',
        items: [],
      })
    }
    const commit = () => {
      if (
        generation.current !== currentGeneration ||
        forbidden ||
        appointmentsFailed ||
        coverageFailed ||
        !appointmentsLoaded ||
        !coverageLoaded
      ) {
        return
      }
      const page = parseTodaySchedule(appointmentValue, bounds)
      const complete = hasCompleteTodayCoverage(
        coverageValue,
        bounds,
        page.items.length
      )
      if (!page.valid) {
        clearCoherenceTimeout()
        commitInvalid()
        return
      }
      if (!complete) {
        setState({ owner: tenantId, boundsKey, status: 'loading', items: [] })
        clearCoherenceTimeout()
        coherenceTimeout = window.setTimeout(
          commitInvalid,
          COVERAGE_COHERENCE_SETTLE_MS
        )
        return
      }
      clearCoherenceTimeout()
      setState({
        owner: tenantId,
        boundsKey,
        status: 'ready',
        items: page.items,
      })
    }
    const unsubscribeAppointments = repository.subscribeTodayAppointments(
      tenantId,
      bounds.startAtMs,
      bounds.endBeforeMs,
      (value) => {
        appointmentValue = value
        appointmentsLoaded = true
        appointmentsFailed = false
        commit()
      },
      handleError('appointments')
    )
    const unsubscribeCoverage = repository.subscribeTodayAppointmentCoverage(
      tenantId,
      (value) => {
        coverageValue = value
        coverageLoaded = true
        coverageFailed = false
        commit()
      },
      handleError('coverage')
    )
    return () => {
      generation.current += 1
      clearCoherenceTimeout()
      unsubscribeAppointments()
      unsubscribeCoverage()
    }
  }, [bounds, boundsKey, repository, tenantId, resume, setState])

  if (timeZone.isError) {
    return {
      status: isPermissionDenied(timeZone.error) ? 'forbidden' : 'error',
      items: [] as AppointmentItem[],
      bounds: null as ClinicDayBounds | null,
    }
  }
  if (timeZone.isSuccess && !bounds) {
    return {
      status: 'invalid' as const,
      items: [] as AppointmentItem[],
      bounds: null as ClinicDayBounds | null,
    }
  }
  if (state.owner !== tenantId || state.boundsKey !== boundsKey) {
    return {
      status: 'loading' as const,
      items: [] as AppointmentItem[],
      bounds,
    }
  }
  return { status: state.status, items: state.items, bounds }
}
